import { Suspense, lazy, useEffect, useRef, useState } from "react";

import {
  ConsentsChangedError,
  consentPreviewUrl,
  getRequiredConsents,
  submitConsents,
} from "src/api/client";
import type { ConsentType, QueueStatus } from "src/api/types";
import { ConsentPdfBoundary, ConsentPdfLoading } from "src/components/ConsentPdfBoundary";
import { StatusToast } from "src/components/StatusToast";

const ConsentPdf = lazy(() => import("src/components/ConsentPdf"));

interface Props {
  clientId: string;
  streamToken: string;
  status: QueueStatus | null;
  consentTypes: ConsentType[];
  state?: string;
  onConsentTypesChange: (types: ConsentType[]) => void;
  onBack: () => void;
  onSubmitted: () => void;
}

const LABELS: Record<ConsentType, string> = {
  TREAT: "Consent to Treat",
  INFORMATION_DISCLOSURE: "Disclosure of Health Information",
  HANDBOOK: "Member Handbook",
};

export function consentLabel(type: ConsentType, state?: string): string {
  if (type === "HANDBOOK" && state) return `Member Handbook (${state})`;
  return LABELS[type] ?? type;
}

export function errorMessage(code: string): string {
  switch (code) {
    case "invalid_or_expired_stream_token":
      return "This page has expired. Please reopen the link we sent you.";
    case "patient_not_identified":
      return "We can't find your record yet. Please check in with our staff.";
    case "hub_unavailable":
      return "Unable to load this document. Please try again in a moment.";
    default:
      return "Something went wrong. Please try again.";
  }
}

const NEEDS_PATIENT = new Set(["invalid_or_expired_stream_token", "patient_not_identified"]);

function sameTypes(a: ConsentType[], b: ConsentType[]): boolean {
  return a.length === b.length && a.every((t) => b.includes(t));
}

function toggle<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function ConsentsView({
  clientId,
  streamToken,
  status,
  consentTypes,
  state,
  onConsentTypesChange,
  onBack,
  onSubmitted,
}: Props) {
  const [checked, setChecked] = useState<Set<ConsentType>>(new Set());
  const [openTypes, setOpenTypes] = useState<Set<ConsentType>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === "completed" || status === "cancelled") onBack();
  }, [status, onBack]);

  useEffect(() => {
    if (error || changed) alertRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [error, changed]);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    setChanged(false);
    try {
      await submitConsents(clientId, streamToken, consentTypes);
    } catch (e) {
      if (e instanceof ConsentsChangedError) {
        applyCurrentTypes(e.consentTypes);
        return;
      }

      const code = e instanceof Error ? e.message : "";
      // Refreshing can't fix these; the patient has to act.
      if (!NEEDS_PATIENT.has(code)) {
        try {
          const current = await getRequiredConsents(clientId, streamToken);
          if (!sameTypes(current.consentTypes, consentTypes)) {
            applyCurrentTypes(current.consentTypes);
            return;
          }
        } catch {
          // fall through to the original error
        }
      }

      setSubmitting(false);
      setError(errorMessage(code));
      return;
    }
    onSubmitted();
  }

  // An empty list means the agreement already went through.
  function applyCurrentTypes(types: ConsentType[]) {
    if (types.length === 0) {
      onSubmitted();
      return;
    }
    onConsentTypesChange(types);
    setChecked(new Set());
    setOpenTypes(new Set());
    setSubmitting(false);
    setChanged(true);
  }

  const allChecked = consentTypes.length > 0 && consentTypes.every((t) => checked.has(t));
  const canSubmit = allChecked && !submitting;

  return (
    <div className="insurance-upload">
      {status === "in_progress" && (
        <StatusToast title="Your nurse is ready" message="Tap here to go back." onClick={onBack} />
      )}

      <button type="button" className="insurance-back" onClick={onBack}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Back
      </button>

      <h1>Sign your consent forms</h1>
      <p>
        Please read and agree to the following documents. These are required consents and we need
        them signed in order to see you.
      </p>

      {consentTypes.map((type) => (
        <div key={type} className="consent-row">
          <div className="consent-row-head">
            <label className="consent-check">
              <input
                type="checkbox"
                checked={checked.has(type)}
                disabled={submitting}
                onChange={() => setChecked((prev) => toggle(prev, type))}
              />
              {consentLabel(type, state)}
            </label>
            <button
              type="button"
              className="btn btn-outline consent-read"
              aria-label={`${openTypes.has(type) ? "Hide" : "Read"} ${consentLabel(type, state)}`}
              onClick={() => setOpenTypes((prev) => toggle(prev, type))}
            >
              {openTypes.has(type) ? "Hide" : "Read"}
            </button>
          </div>
          {openTypes.has(type) && (
            <ConsentPdfBoundary consentType={type}>
              <Suspense fallback={<ConsentPdfLoading />}>
                <ConsentPdf
                  url={consentPreviewUrl(clientId, streamToken, type)}
                  consentType={type}
                />
              </Suspense>
            </ConsentPdfBoundary>
          )}
        </div>
      ))}

      {changed && (
        <div
          ref={alertRef}
          role="alert"
          className="status-alert status-alert--warning insurance-error"
        >
          <p>Your documents have changed. Please review the list and try again.</p>
        </div>
      )}

      {error && (
        <div
          ref={alertRef}
          role="alert"
          className="status-alert status-alert--warning insurance-error"
        >
          <p>{error}</p>
        </div>
      )}

      {!allChecked && consentTypes.length > 0 && (
        <p className="consent-hint">Check each box to sign these consent forms.</p>
      )}

      <button
        type="button"
        className="btn btn-primary btn-full"
        disabled={!canSubmit}
        onClick={() => void handleSubmit()}
      >
        {submitting ? "Please wait..." : "Agree and continue"}
      </button>
    </div>
  );
}
